import { useCallback, useEffect, useState } from 'react';
import RegisterUserService from '../../services/RegisterUserService';
import WorkspaceSettingsService from '../../services/WorkspaceSettingsService';

const inp = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: 10,
  border: '1px solid #e9ecef',
  fontSize: 14,
  color: '#344767',
  background: '#fff',
};

function Toggle({ checked, onChange, disabled, label, hint, compact }) {
  return (
    <div style={{ marginBottom: compact ? 0 : hint ? 8 : 16, flex: compact ? '1 1 220px' : undefined, minWidth: compact ? 200 : undefined }}>
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.55 : 1,
        }}
      >
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          disabled={disabled}
          onClick={() => !disabled && onChange(!checked)}
          style={{
            width: 44,
            height: 24,
            borderRadius: 999,
            border: 'none',
            padding: 2,
            flexShrink: 0,
            background: checked ? '#5e72e4' : '#cbd5e0',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          <span
            style={{
              display: 'block',
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: '#fff',
              boxShadow: '0 1px 4px rgba(0,0,0,.2)',
              transform: checked ? 'translateX(20px)' : 'translateX(0)',
            }}
          />
        </button>
        <span style={{ fontWeight: 700, color: '#344767', fontSize: compact ? 13 : 14 }}>{label}</span>
      </label>
      {hint && (
        <p style={{ fontSize: 11, color: '#adb5bd', margin: '6px 0 0 56px', lineHeight: 1.45 }}>{hint}</p>
      )}
    </div>
  );
}

function ToggleRow({ children, hint }) {
  return (
    <div style={{ marginBottom: hint ? 4 : 18 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 24px', alignItems: 'flex-start' }}>
        {children}
      </div>
      {hint && (
        <p style={{ fontSize: 11, color: '#adb5bd', margin: '8px 0 0', lineHeight: 1.45 }}>{hint}</p>
      )}
    </div>
  );
}

function EmployeeGrid({ employees, selectedIds, onToggle, disabled, emptyMessage }) {
  if (employees.length === 0) {
    return <p style={{ color: '#8898aa', fontSize: 13, margin: '0 0 8px' }}>{emptyMessage}</p>;
  }
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: 8,
      }}
    >
      {employees.map((emp) => {
        const empId = emp.id ?? emp.Id;
        const name = `${emp.firstName ?? emp.FirstName ?? ''} ${emp.lastName ?? emp.LastName ?? ''}`.trim();
        const allSelected = selectedIds.length === 0;
        const explicit = selectedIds.length > 0 && selectedIds.includes(empId);
        const active = allSelected || explicit;
        return (
          <label
            key={empId}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '9px 12px',
              borderRadius: 10,
              border: `1.5px solid ${active ? '#5e72e4' : '#e9ecef'}`,
              background: active ? '#f0f2ff' : '#fafbfc',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.55 : 1,
            }}
          >
            <input
              type="checkbox"
              checked={active}
              disabled={disabled}
              onChange={() => onToggle(empId)}
              style={{ accentColor: '#5e72e4' }}
            />
            <span style={{ fontWeight: 600, color: '#344767', fontSize: 13 }}>{name || `#${empId}`}</span>
          </label>
        );
      })}
    </div>
  );
}

function SchedulerSection({ icon, iconBg, title, subtitle, children }) {
  return (
    <div
      style={{
        border: '1px solid #eef1f6',
        borderRadius: 14,
        padding: '20px 22px',
        marginBottom: 20,
        background: 'linear-gradient(180deg, #fafbfe 0%, #fff 48px)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 18 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 18,
            flexShrink: 0,
          }}
        >
          <i className={icon} />
        </div>
        <div>
          <h5 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#344767' }}>{title}</h5>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#8898aa', lineHeight: 1.55 }}>{subtitle}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function toggleEmployeeInList(allEmployees, employeeId, currentIds) {
  const allIds = allEmployees.map((emp) => emp.id ?? emp.Id);
  if (currentIds.length === 0) {
    return allIds.filter((id) => id !== employeeId);
  }
  const set = new Set(currentIds);
  if (set.has(employeeId)) set.delete(employeeId);
  else set.add(employeeId);
  const next = Array.from(set);
  if (next.length === allIds.length && allIds.every((id) => next.includes(id))) {
    return [];
  }
  return next;
}

export default function ClientSchedulerSettingsPanel({ clientId, canEdit = true, onMessage }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoAssign, setAutoAssign] = useState({
    enabled: false,
    employeeIds: [],
    strategy: 'RoundRobin',
    notifyEnabled: true,
  });
  const [inactivity, setInactivity] = useState({
    enabled: false,
    thresholdHours: 24,
    employeeIds: [],
    notifyEnabled: true,
  });

  const notify = (msg, type = 'success') => {
    if (typeof onMessage === 'function') onMessage(msg, type);
  };

  const loadSettings = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [metaSettings, inactivitySettings, userList] = await Promise.all([
        WorkspaceSettingsService.getMetaLeadAutoAssignSettings(clientId),
        WorkspaceSettingsService.getLeadInactivityReassignSettings(clientId),
        RegisterUserService.getUsersByClientId(clientId),
      ]);
      const employeeList = (Array.isArray(userList) ? userList : []).filter(
        (u) => (u.roleId ?? u.RoleId) === 4,
      );
      setEmployees(employeeList);

      const metaIds = metaSettings?.employeeIds ?? metaSettings?.EmployeeIds ?? [];
      setAutoAssign({
        enabled: !!(metaSettings?.enabled ?? metaSettings?.Enabled),
        employeeIds: Array.isArray(metaIds) ? metaIds : [],
        strategy: metaSettings?.strategy ?? metaSettings?.Strategy ?? 'RoundRobin',
        notifyEnabled: metaSettings?.notifyEnabled ?? metaSettings?.NotifyEnabled ?? true,
      });

      const inactIds = inactivitySettings?.employeeIds ?? inactivitySettings?.EmployeeIds ?? [];
      setInactivity({
        enabled: !!(inactivitySettings?.enabled ?? inactivitySettings?.Enabled),
        thresholdHours: inactivitySettings?.thresholdHours ?? inactivitySettings?.ThresholdHours ?? 24,
        employeeIds: Array.isArray(inactIds) ? inactIds : [],
        notifyEnabled: inactivitySettings?.notifyEnabled ?? inactivitySettings?.NotifyEnabled ?? true,
      });
    } catch {
      notify('Could not load scheduler settings.', 'error');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveSettings = async () => {
    if (!clientId || !canEdit) return;
    setSaving(true);
    try {
      await Promise.all([
        WorkspaceSettingsService.updateMetaLeadAutoAssignSettings(clientId, {
          enabled: autoAssign.enabled,
          employeeIds: autoAssign.employeeIds,
          strategy: autoAssign.strategy,
          notifyEnabled: autoAssign.notifyEnabled,
        }),
        WorkspaceSettingsService.updateLeadInactivityReassignSettings(clientId, {
          enabled: inactivity.enabled,
          thresholdHours: Math.max(1, parseInt(inactivity.thresholdHours, 10) || 24),
          employeeIds: inactivity.employeeIds,
          notifyEnabled: inactivity.notifyEnabled,
        }),
      ]);
      notify('Scheduler settings saved.');
      loadSettings();
    } catch {
      notify('Save failed. You may need Admin-level permissions.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const strategyCard = (value, title, desc) => {
    const on = autoAssign.strategy === value;
    return (
      <label
        style={{
          flex: '1 1 0',
          minWidth: 200,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          padding: '12px 14px',
          borderRadius: 10,
          border: `1.5px solid ${on ? '#5e72e4' : '#e9ecef'}`,
          background: on ? '#f0f2ff' : '#fff',
          cursor: canEdit && autoAssign.enabled ? 'pointer' : 'not-allowed',
          opacity: canEdit && autoAssign.enabled ? 1 : 0.55,
        }}
      >
        <input
          type="radio"
          name={`autoAssignStrategy-${clientId}`}
          checked={on}
          disabled={!canEdit || !autoAssign.enabled}
          onChange={() => setAutoAssign((p) => ({ ...p, strategy: value }))}
          style={{ marginTop: 3, accentColor: '#5e72e4' }}
        />
        <span>
          <span style={{ fontWeight: 700, color: '#344767', display: 'block', fontSize: 13 }}>{title}</span>
          <span style={{ fontSize: 12, color: '#8898aa' }}>{desc}</span>
        </span>
      </label>
    );
  };

  if (!clientId) {
    return <p style={{ color: '#8898aa' }}>No agency selected.</p>;
  }

  if (loading) {
    return (
      <p style={{ color: '#8392ab', textAlign: 'center', padding: 32 }}>
        <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }} />
        Loading scheduler settings…
      </p>
    );
  }

  return (
    <>
      <SchedulerSection
        icon="fab fa-facebook-f"
        iconBg="linear-gradient(135deg,#1877f2,#0d65d9)"
        title="Meta lead auto-assign"
        subtitle="Automatically assign Facebook / Instagram leads to employees when imported via scheduler or webhook."
      >
        <ToggleRow hint='Notify message e.g. "3 new leads assigned to you. Please check."'>
          <Toggle
            compact
            checked={autoAssign.enabled}
            disabled={!canEdit}
            onChange={(v) => setAutoAssign((p) => ({ ...p, enabled: v }))}
            label="Enable Meta auto-assign"
          />
          <Toggle
            compact
            checked={autoAssign.notifyEnabled}
            disabled={!canEdit || !autoAssign.enabled}
            onChange={(v) => setAutoAssign((p) => ({ ...p, notifyEnabled: v }))}
            label="Notify on assign"
          />
        </ToggleRow>

        <p style={{ fontWeight: 700, color: '#344767', fontSize: 13, margin: '0 0 10px' }}>Allocation rule</p>
        <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 18 }}>
          {strategyCard('EqualSplit', 'Equal split', 'Divide each batch evenly (20 ÷ 5 = 4 each).')}
          {strategyCard('RoundRobin', 'Round robin', 'One lead at a time — best for webhooks.')}
        </div>

        <p style={{ fontWeight: 700, color: '#344767', fontSize: 13, margin: '0 0 10px' }}>Employee pool</p>
        <EmployeeGrid
          employees={employees}
          selectedIds={autoAssign.employeeIds}
          disabled={!canEdit || !autoAssign.enabled}
          emptyMessage="No active employees. Add employees under Register User first."
          onToggle={(empId) => setAutoAssign((p) => ({ ...p, employeeIds: toggleEmployeeInList(employees, empId, p.employeeIds) }))}
        />
      </SchedulerSection>

      <SchedulerSection
        icon="fas fa-user-clock"
        iconBg="linear-gradient(135deg,#fb6340,#f5365c)"
        title="Inactivity re-assign"
        subtitle="Re-assign leads when an employee takes no action within the set time."
      >
        <ToggleRow>
          <Toggle
            compact
            checked={inactivity.enabled}
            disabled={!canEdit}
            onChange={(v) => setInactivity((p) => ({ ...p, enabled: v }))}
            label="Enable inactivity re-assign"
          />
          <Toggle
            compact
            checked={inactivity.notifyEnabled}
            disabled={!canEdit || !inactivity.enabled}
            onChange={(v) => setInactivity((p) => ({ ...p, notifyEnabled: v }))}
            label="Notify new assignee"
          />
        </ToggleRow>

        <div style={{ marginBottom: 16, maxWidth: 220, marginTop: 4 }}>
          <label style={{ display: 'block', fontWeight: 700, color: '#344767', fontSize: 13, marginBottom: 8 }}>
            No-action threshold (hours)
          </label>
          <input
            type="number"
            min={1}
            max={720}
            disabled={!canEdit || !inactivity.enabled}
            value={inactivity.thresholdHours}
            onChange={(e) => setInactivity((p) => ({ ...p, thresholdHours: e.target.value }))}
            style={inp}
          />
        </div>

        <p style={{ fontWeight: 700, color: '#344767', fontSize: 13, margin: '0 0 10px' }}>Re-assign rotation</p>
        <EmployeeGrid
          employees={employees}
          selectedIds={inactivity.employeeIds}
          disabled={!canEdit || !inactivity.enabled}
          emptyMessage="No active employees. At least 2 are required for re-assign."
          onToggle={(empId) => setInactivity((p) => ({ ...p, employeeIds: toggleEmployeeInList(employees, empId, p.employeeIds) }))}
        />
      </SchedulerSection>

      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid #f0f2f5' }}>
        <button
          type="button"
          disabled={!canEdit || saving}
          onClick={saveSettings}
          style={{
            padding: '12px 28px',
            borderRadius: 10,
            border: 'none',
            background: saving ? '#adb5bd' : 'linear-gradient(135deg,#5e72e4,#825ee4)',
            color: '#fff',
            fontWeight: 700,
            fontSize: 14,
            cursor: canEdit && !saving ? 'pointer' : 'not-allowed',
          }}
        >
          {saving ? 'Saving…' : 'Save scheduler settings'}
        </button>
      </div>
    </>
  );
}
